import { Button, LogoMark, type Product, Sheet, Toaster, useMediaQuery, wideLayout } from "@nexxt/ui";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { type ReactNode, useState } from "react";
import { m } from "../paraglide/messages.js";
import { useUndoShortcut } from "../undo/undo-shortcut";

// Frame of every screen of an app (ADR-050): from `lg` the sidebar is a column; below, a top bar with the menu, the
// logo and what the app puts there (search, user), and the sidebar in a sheet from the left. The skip link, the page
// and the toasts. What goes in the sidebar and in the top bar is the app's: Backstage and Atelier differ there.

interface AppFrameProps {
  product: Product;
  /** The sidebar, as a column from `lg` or inside the sheet; `closeMenu` closes the sheet before a dialog opens. */
  sidebar: (props: { variant: "column" | "sheet"; closeMenu: () => void }) => ReactNode;
  /** The right of the top bar below `lg`. */
  topBar: (props: { closeMenu: () => void }) => ReactNode;
  /** What opens over the page: the palette, the list of shortcuts. */
  children?: ReactNode;
}

export function AppFrame({ product, sidebar, topBar, children }: AppFrameProps) {
  const href = useRouterState({ select: (s) => s.location.href });
  const wide = useMediaQuery(wideLayout);
  // the toasts are here, and so is the key of their "Undo" (ADR-282)
  useUndoShortcut();
  // the menu stays open only on the page where it was opened: any navigation (link, ⌘K, g + key) closes it
  const [menuOpenAt, setMenuOpenAt] = useState<string | null>(null);
  const menuOpen = !wide && menuOpenAt === href;
  const closeMenu = () => setMenuOpenAt(null);

  return (
    <div className="flex h-dvh flex-col overflow-hidden lg:flex-row">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-70 focus:rounded-md focus:bg-background focus:px-3 focus:py-2"
      >
        {m.skip_to_content()}
      </a>
      <header className="z-10 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-muted px-2 lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          aria-label={m.nav_open_menu()}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpenAt(href)}
        >
          <Menu aria-hidden />
        </Button>
        <LogoMark product={product} nameClassName="hidden sm:block" productClassName="hidden sm:block" />
        {topBar({ closeMenu })}
      </header>
      {wide && sidebar({ variant: "column", closeMenu })}
      <Sheet
        open={menuOpen}
        onOpenChange={(open) => setMenuOpenAt(open ? href : null)}
        side="left"
        title={m.nav_label()}
        className="w-72 max-w-[85vw] bg-muted"
      >
        {sidebar({ variant: "sheet", closeMenu })}
      </Sheet>
      {/*
        the page scrolls here, and a page of text alone (a home with nothing to do at 400% zoom) holds nothing to
        focus: the area itself takes the focus, so the keyboard scrolls it too (WCAG 2.1.1, as ScrollRegion)
      */}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable from the keyboard */}
      <main id="main" tabIndex={0} className="min-h-0 min-w-0 flex-1 overflow-auto focus-visible:outline-offset-[-2px]">
        <Outlet />
      </main>
      {children}
      <Toaster />
    </div>
  );
}
